import React from 'react';
import { Chip, MonatsPips, PersonAvatar, saldoAnzeige, type AvatarTon } from '../../../../components/immobilien/ui';
import { eur, initialen } from '../../../../lib/immobilien/format';
import { deDatum } from '../../../../lib/immobilien/objektDetail';
import { EIG_STATUS, PORTAL_ANZEIGE, statusText, type EigZeile } from '../../../../lib/eigentuemer/liste';

/** Avatar: Privatperson --acc, Firma --info, ehemalig --fill2/--ink3 (README §4). */
export function avatarTon(z: Pick<EigZeile, 'firma' | 'status'>): AvatarTon {
  return z.status === 'ehemalig' ? 'grau' : z.firma ? 'info' : 'acc';
}

/** „Hausgeld · seit …“ bzw. „Auszahlung · seit …“, bei Übernahme/ehemalig das Datum. */
export function seitText(z: Pick<EigZeile, 'status' | 'beginn' | 'endetAm' | 'werte'>): string {
  if (z.status === 'uebernahme' && z.beginn) return `Übernahme ${deDatum(z.beginn)}`;
  if (z.status === 'ehemalig') return 'Ehemalig';
  const art = z.werte.saldoArt === 'hausgeld' || !z.werte.verwaltet ? 'Hausgeld' : 'Auszahlung';
  return z.beginn ? `${art} · seit ${deDatum(z.beginn)}` : art;
}

/** Betrag „Monatlich“: Hausgeld oder Auszahlung; ohne hinterlegte Vergütung mit Hinweis. */
export function monatlichTitel(z: Pick<EigZeile, 'werte'>): string | undefined {
  return z.werte.verwaltet && z.werte.vergM === null ? 'Auszahlung ohne Verwaltervergütung – Vergütung je Einheit noch nicht hinterlegt' : undefined;
}

export const EigentuemerKachel = React.memo(function EigentuemerKachel({ z, onOeffnen }: { z: EigZeile; onOeffnen: (id: string) => void }) {
  const st = EIG_STATUS[z.status];
  const saldo = saldoAnzeige(z.saldo);
  const portal = PORTAL_ANZEIGE[z.portal];
  const tastatur = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOeffnen(z.id); }
  };
  return (
    <article role="link" tabIndex={0} aria-label={`${z.name} öffnen`} onClick={() => onOeffnen(z.id)} onKeyDown={tastatur}
      className="immo-glas immo-hover-kachel flex flex-col cursor-pointer min-w-0" style={{ borderRadius: 26, padding: 16, gap: 14 }}>
      <div className="flex items-center min-w-0" style={{ gap: 12 }}>
        <PersonAvatar text={initialen(z.name)} ton={avatarTon(z)} punkt={{ farbe: portal.farbe, label: portal.label }} />
        <div className="flex flex-col flex-1 min-w-0" style={{ gap: 1 }}>
          <span className="truncate" style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.01em' }}>{z.name}</span>
          <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{[z.objektText, z.rollenText].filter(Boolean).join(' · ')}</span>
        </div>
        <Chip ton={st.ton} punkt>{statusText(z)}</Chip>
      </div>
      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div className="flex flex-col min-w-0" title={monatlichTitel(z)} style={{ borderRadius: 16, padding: '10px 12px', gap: 1, background: 'var(--fill)' }}>
          <span style={{ fontSize: 11, color: 'var(--ink3)' }}>Monatlich</span>
          <b className="immo-zahl" style={{ fontSize: 17, fontWeight: 800 }}>{z.werte.nEinheiten ? eur(z.werte.monatlich, { nachkomma: 0 }) : '–'}</b>
        </div>
        <div className="flex flex-col min-w-0" title={saldo.titel}
          style={{ borderRadius: 16, padding: '10px 12px', gap: 1, background: saldo.rueckstand ? 'var(--bad-soft)' : 'var(--fill)' }}>
          <span style={{ fontSize: 11, color: 'var(--ink3)' }}>Eigentümerkonto</span>
          <b className="immo-zahl truncate" style={{ fontSize: 17, fontWeight: 800, color: saldo.farbe }}>{saldo.text}</b>
        </div>
      </div>
      <div className="flex items-center" style={{ gap: 8 }}>
        <MonatsPips pips={null} />
        <span className="whitespace-nowrap" style={{ fontSize: 11, color: 'var(--ink3)' }}>{seitText(z)}</span>
      </div>
    </article>
  );
});
